import React, { useState, useEffect, Fragment } from "react";
import "../styles/registerDevice.css";
import { handleProvisionCharger, handleVerifyCharger, handlePairCharger, getChargerByMacId } from "../service/adminUI";
import { IonButton, IonCardContent, IonTitle } from "@ionic/react";
import SessionManager from "../service/SessionManager";
import { formatMacId } from "../utils/formatMacId";

const STEP_REGISTER = 1;
const STEP_VERIFY   = 2;
const STEP_PAIR     = 3;
const STEP_DONE     = 4;

const STEP_LABELS = ['Register', 'Verify', 'Pair', 'Done'];

const RegisterCharger = ({ bckimgChange, setRegisterToast }) => {
  const TOAST_ERROR   = "toast toast-error";
  const TOAST_SUCCESS = "toast toast-success";

  const [step,    setStep]    = useState(STEP_REGISTER);
  const [loading, setLoading] = useState(false);

  // Step 1 form inputs
  const [macId,           setMacId]           = useState('');
  const [serialNumber,    setSerialNumber]     = useState('');
  const [chargerModel,    setChargerModel]     = useState('CALYAN-CP-001');
  const [firmwareVersion, setFirmwareVersion]  = useState('');

  // Step 1 result — shown in Step 2 verify screen
  const [deviceCode, setDeviceCode] = useState('');
  const [userCode,   setUserCode]   = useState('');

  // Step 3 input
  const [ipgMacId, setIpgMacId] = useState('');

  // Charger lookup state
  const [chargerExists, setChargerExists] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);

  useEffect(() => { bckimgChange("registerDevice.png"); }, []);

  // ── Lookup existing charger by MAC ID ────────────────────────────────
  const lookupCharger = async (mac) => {
    if (!mac || !mac.trim()) return;
    setLookingUp(true);
    try {
      const thing = await getChargerByMacId(mac.trim(), SessionManager.getAccessToken());
      if (thing && thing.attributes) {
        const attrs = thing.attributes;
        setChargerExists(true);
        if (attrs.serialNumber) setSerialNumber(attrs.serialNumber);
        if (attrs.model) setChargerModel(attrs.model);
        // firmwareVersion may be in deviceStatus feature or attributes
        const fwVersion = thing.features?.deviceStatus?.properties?.fwVersion;
        if (fwVersion) setFirmwareVersion(fwVersion);

        const status = attrs.status;
        const provProps = thing.features?.provisioning?.properties;
        if (status === 'pending_verification') {
          // Admin already clicked Verify, but the charger pod never polled
          // /devicePoll. Re-hydrate the original codes so admin can re-program
          // the charger if needed; stay on Step 2 instead of jumping to Pair.
          if (provProps?.device_code) setDeviceCode(provProps.device_code);
          if (provProps?.user_code)   setUserCode(provProps.user_code);
          setStep(STEP_VERIFY);
          setRegisterToast({
            css: TOAST_SUCCESS,
            message: 'Charger was verified by admin but has not yet contacted RMS. Re-program the device code into the charger if needed, then wait for it to come online.',
          });
        } else if (status === 'verified' || status === 'active') {
          setRegisterToast({ css: TOAST_SUCCESS, message: `Charger found (status: ${status}). Skipping to Pair step.` });
          setStep(STEP_PAIR);
        } else if (status === 'paired') {
          // FIX(TC-WEB-ADMIN-004): already-paired chargers previously surfaced only
          // a 5-second toast, so automated flows timed out waiting for a persistent
          // success signal.  Advance to STEP_DONE and hydrate ipgMacId so the
          // "fully provisioned" summary is shown durably.
          if (attrs.pairedIpgMacId) {
            setIpgMacId(attrs.pairedIpgMacId);
          }
          setRegisterToast({ css: TOAST_SUCCESS, message: `Charger is already paired with IPG ${attrs.pairedIpgMacId || 'unknown'}` });
          setStep(STEP_DONE);
        } else {
          setRegisterToast({ css: TOAST_SUCCESS, message: `Charger found (status: ${status}). Fields populated.` });
        }
      } else {
        setChargerExists(false);
      }
    } catch (err) {
      console.error('Charger lookup failed:', err);
      setChargerExists(false);
    }
    setLookingUp(false);
  };

  // ── Step 1: Register ───────────────────────────────────────────────
  const doRegister = async () => {
    if (!macId.trim() || !serialNumber.trim() || !chargerModel.trim()) {
      setRegisterToast({ css: TOAST_ERROR, message: 'MAC ID, Serial Number and Model are required' });
      return;
    }
    setLoading(true);
    try {
      const result = await handleProvisionCharger(
        macId.trim(), chargerModel.trim(), serialNumber.trim(),
        firmwareVersion.trim(), SessionManager.getAccessToken()
      );
      if (result.device_code) {
        setDeviceCode(result.device_code);
        setUserCode(result.user_code);
        setStep(STEP_VERIFY);
      } else {
        setRegisterToast({ css: TOAST_ERROR, message: 'Registration failed: ' + (result.error || 'unknown error') });
      }
    } catch (err) {
      setRegisterToast({ css: TOAST_ERROR, message: 'Error: ' + err.message });
    }
    setLoading(false);
  };

  // ── Step 2: Verify ─────────────────────────────────────────────────
  const doVerify = async () => {
    setLoading(true);
    try {
      const result = await handleVerifyCharger(userCode, SessionManager.getAccessToken());
      if (result.success) {
        setStep(STEP_PAIR);
        setRegisterToast({ css: TOAST_SUCCESS, message: 'Charger verified successfully' });
      } else {
        setRegisterToast({ css: TOAST_ERROR, message: 'Verification failed: ' + (result.error || 'unknown') });
      }
    } catch (err) {
      setRegisterToast({ css: TOAST_ERROR, message: 'Error: ' + err.message });
    }
    setLoading(false);
  };

  // ── Step 3: Pair ───────────────────────────────────────────────────
  const doPair = async () => {
    if (!ipgMacId.trim()) {
      setRegisterToast({ css: TOAST_ERROR, message: 'IPG MAC ID is required' });
      return;
    }
    setLoading(true);
    try {
      const result = await handlePairCharger(macId.trim(), ipgMacId.trim(), SessionManager.getAccessToken());
      if (result.success) {
        setStep(STEP_DONE);
        setRegisterToast({ css: TOAST_SUCCESS, message: 'Charger paired with IPG successfully' });
      } else {
        setRegisterToast({ css: TOAST_ERROR, message: 'Pairing failed: ' + (result.error || 'unknown') });
      }
    } catch (err) {
      setRegisterToast({ css: TOAST_ERROR, message: 'Error: ' + err.message });
    }
    setLoading(false);
  };

  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setRegisterToast({ css: TOAST_SUCCESS, message: 'Copied!' });
    } catch (err) {
      setRegisterToast({ css: TOAST_ERROR, message: 'Copy failed — please select and copy manually' });
    }
  };

  const doReset = () => {
    setStep(STEP_REGISTER);
    setMacId(''); setSerialNumber(''); setChargerModel('CALYAN-CP-001');
    setFirmwareVersion(''); setDeviceCode(''); setUserCode(''); setIpgMacId('');
    setChargerExists(false);
  };

  return (
    <Fragment>
      <IonTitle className="device-title">Register Charger Pod</IonTitle>

      {/* Step progress indicator */}
      <div className="charger-steps">
        {STEP_LABELS.map((label, i) => (
          <span
            key={i}
            className={
              'charger-step' +
              (step === i + 1 ? ' charger-step-active' : '') +
              (step > i + 1  ? ' charger-step-done'   : '')
            }
          >
            <span className="charger-step-num">{i + 1}</span>
            <span className="charger-step-label">{label}</span>
          </span>
        ))}
      </div>

      {/* ── STEP 1: Register form ─────────────────────────────────── */}
      {step === STEP_REGISTER && (
        <IonCardContent className="device-content">
          <form className="device-form charger-provision-form" onSubmit={e => e.preventDefault()}>
            <div className="field-row">
              <label className="field-label">MAC ID *</label>
              <input
                data-testid="TCHARGER_MAC"
                className="field-input"
                placeholder="AA-BB-CC-DD-EE-FF"
                value={macId}
                onChange={e => { setMacId(formatMacId(e.target.value)); setChargerExists(false); }}
                onBlur={e => lookupCharger(e.target.value)}
              />
              {lookingUp && <span style={{ fontSize: '12px', color: '#999', marginLeft: '8px' }}>Looking up charger...</span>}
            </div>
            <div className="field-row">
              <label className="field-label">Serial Number *</label>
              <input
                data-testid="TCHARGER_SERIAL"
                className="field-input"
                placeholder="CP-2024-00001"
                value={serialNumber}
                onChange={e => setSerialNumber(e.target.value)}
              />
            </div>
            <div className="field-row">
              <label className="field-label">Charger Model *</label>
              <input
                data-testid="TCHARGER_MODEL"
                className="field-input"
                placeholder="CALYAN-CP-001"
                value={chargerModel}
                onChange={e => setChargerModel(e.target.value)}
              />
            </div>
            <div className="field-row">
              <label className="field-label">Firmware Version</label>
              <input
                data-testid="TCHARGER_FIRMWARE"
                className="field-input"
                placeholder="1.0.0"
                value={firmwareVersion}
                onChange={e => setFirmwareVersion(e.target.value)}
              />
            </div>
          </form>
          <div className="homeMainFooter">
            <IonButton data-testid="TCHARGER_REGISTER" class="loginButton" fill="solid" shape="round" disabled={loading} onClick={doRegister}>
              {loading ? 'Registering...' : 'Register'}
            </IonButton>
          </div>
        </IonCardContent>
      )}

      {/* ── STEP 2: Show device_code / user_code, then Verify ─────── */}
      {step === STEP_VERIFY && (
        <IonCardContent className="device-content">
          {chargerExists ? (
            <p className="provision-info" style={{ background: '#fff7e0', border: '1px solid #f0c040', padding: '8px 12px', borderRadius: '4px' }}>
              <strong>Waiting for charger to come online.</strong>{' '}
              This charger was previously verified by an admin, but the charger pod
              has not yet contacted RMS. Re-program the device code below into the
              charger if needed, then use <em>Refresh Status</em> to check again.
            </p>
          ) : (
            <p className="provision-info">
              Charger registered. Program the <strong>Device Code</strong> into the
              charger NVS via serial cable, then click <em>Verify Device</em>.
            </p>
          )}
          <div className="code-card">
            <div className="code-entry">
              <span className="code-label">Device Code <span className="code-hint">(program into NVS)</span></span>
              <div className="code-value-row">
                <code className="code-value">{deviceCode}</code>
                <button type="button" className="copy-btn" onClick={() => copyToClipboard(deviceCode)}>Copy</button>
              </div>
            </div>
            <div className="code-entry">
              <span className="code-label">User Code <span className="code-hint">(for verification)</span></span>
              <div className="code-value-row">
                <code className="code-value user-code-highlight">{userCode}</code>
                <button type="button" className="copy-btn" onClick={() => copyToClipboard(userCode)}>Copy</button>
              </div>
            </div>
          </div>
          <div className="homeMainFooter">
            {chargerExists ? (
              <IonButton
                data-testid="TCHARGER_REFRESH"
                class="loginButton"
                fill="solid"
                shape="round"
                disabled={lookingUp}
                onClick={() => lookupCharger(macId)}
              >
                {lookingUp ? 'Checking...' : 'Refresh Status'}
              </IonButton>
            ) : (
              <IonButton data-testid="TCHARGER_VERIFY" class="loginButton" fill="solid" shape="round" disabled={loading} onClick={doVerify}>
                {loading ? 'Verifying...' : 'Verify Device'}
              </IonButton>
            )}
          </div>
        </IonCardContent>
      )}

      {/* ── STEP 3: Pair with IPG ─────────────────────────────────── */}
      {step === STEP_PAIR && (
        <IonCardContent className="device-content">
          <p className="provision-info">
            Charger <strong>{macId}</strong> verified. Enter the IPG MAC address to pair.
          </p>
          <form className="device-form charger-provision-form" onSubmit={e => e.preventDefault()}>
            <div className="field-row">
              <label className="field-label">IPG MAC ID *</label>
              <input
                data-testid="TCHARGER_IPG_MAC"
                className="field-input"
                placeholder="AA-BB-CC-DD-EE-FF"
                value={ipgMacId}
                onChange={e => setIpgMacId(formatMacId(e.target.value))}
              />
            </div>
          </form>
          <div className="homeMainFooter">
            <IonButton data-testid="TCHARGER_PAIR" class="loginButton" fill="solid" shape="round" disabled={loading} onClick={doPair}>
              {loading ? 'Pairing...' : 'Pair with IPG'}
            </IonButton>
          </div>
        </IonCardContent>
      )}

      {/* ── STEP 4: Done ─────────────────────────────────────────── */}
      {step === STEP_DONE && (
        <IonCardContent className="device-content">
          <div className="provision-done">
            <div className="provision-done-icon">&#10003;</div>
            <p>
              Charger <strong>{macId}</strong> is fully provisioned and paired
              with IPG <strong>{ipgMacId}</strong>.
            </p>
            <p className="provision-done-sub">The charger is now ready for WiFi configuration.</p>
          </div>
          <div className="homeMainFooter">
            <IonButton class="loginButton" fill="solid" shape="round" onClick={doReset}>
              Register Another
            </IonButton>
          </div>
        </IonCardContent>
      )}
    </Fragment>
  );
};

export default RegisterCharger;
